import { DecoratorNode } from 'lexical';
import { twJoin } from 'tailwind-merge';

import Spinner from 'shopper/components/Spinner';

import Image from 'components/Image';

import { toFallbackImageAltText } from 'lib/image';

const STYLES = 'max-h-[200px] w-auto max-w-3/5';

class ImageNode extends DecoratorNode {
  __src;
  __height;
  __width;

  constructor(src, height, width, key) {
    super(key);

    this.__src = src;
    this.__height = height || 'inherit';
    this.__width = width || 'inherit';
  }

  static clone({ src, height, width, key }) {
    return new ImageNode(src, height, width, key);
  }

  static getType() {
    return 'image';
  }

  static importDOM() {
    return {
      img: () => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    };
  }

  static importJSON(serializedNode) {
    return $createImageNode(serializedNode);
  }

  createDOM() {
    return document.createElement('div');
  }

  exportDOM() {
    const element = document.createElement('img');

    element.setAttribute('src', this.src);
    element.setAttribute('height', this.height);
    element.setAttribute('width', this.width);
    element.setAttribute('class', STYLES);
    element.setAttribute('loading', 'lazy');

    return { element };
  }

  updateDOM() {
    return false;
  }

  exportJSON() {
    return {
      height: this.height,
      src: this.src,
      type: 'image',
      width: this.width,
    };
  }

  get src() {
    return this.__src;
  }

  get height() {
    return this.__height.toString();
  }

  get width() {
    return this.__width.toString();
  }

  decorate() {
    return (
      <div className="relative min-h-6 w-fit min-w-24">
        <div className="absolute left-1/2 top-1/2 z-1">
          <Spinner className="size-8" />
        </div>
        <Image
          alt={toFallbackImageAltText()}
          className={twJoin(STYLES, 'relative z-2')}
          height={this.height}
          src={this.src}
          width={this.width}
        />
      </div>
    );
  }
}

const convertImageElement = (domNode) => {
  if (domNode instanceof HTMLImageElement) {
    const { src, height, width } = domNode;

    return {
      node: $createImageNode({ src, height, width }),
    };
  }

  return null;
};

export const $createImageNode = ({ src, height, width, key }) =>
  new ImageNode(src, height, width, key);

export const $isImageNode = (node) => node instanceof ImageNode;

export const $addImageNodeConfig = (nodes) => {
  nodes.push(ImageNode);
};

export default ImageNode;
